import { resolveIframeUrl } from '../../../lib/embed-contracts/resolve-iframe-url'
import type { NormalizedSDKConfig, SDKResizeOptions } from '../../../lib/embed-contracts/sdk-config'
import { ensureHTMLElement } from '../utils/dom'

function formatDimension(value: number | string | undefined, fallback: string) {
  if (value === undefined) return fallback
  return typeof value === 'number' ? `${value}px` : value
}

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '')
}

function ensureLeadingSlash(value: string) {
  if (!value) return ''
  return value.startsWith('/') ? value : `/${value}`
}

function resolveLauncherLogoUrl(config: Pick<NormalizedSDKConfig, 'baseUrl' | 'pathPrefix'>) {
  const baseUrl = trimTrailingSlash(config.baseUrl)
  const pathPrefix = trimTrailingSlash(ensureLeadingSlash(config.pathPrefix))
  return `${baseUrl}${pathPrefix}/logo.svg`
}

const CHAT_FE_LAUNCHER_ID = 'float-btn_abo_is_nice'
const DEFAULT_LAUNCHER_RIGHT = 0
const AVOID_CHAT_FE_LAUNCHER_RIGHT = 88
const LAUNCHER_POSITION_STORAGE_KEY = 'ecmas-jssdk-launcher-position'
const LAUNCHER_VERTICAL_MARGIN = 24
const LAUNCHER_DRAG_THRESHOLD = 3

type LauncherPosition = {
  left: number
  top: number
}

export class IframeManager {
  private iframe: HTMLIFrameElement | null = null
  private floatingRoot: HTMLDivElement | null = null
  private backdrop: HTMLDivElement | null = null
  private shell: HTMLDivElement | null = null
  private launcherButton: HTMLButtonElement | null = null
  private isFloatingOpen = false
  private isLauncherHovered = false
  private isLauncherVisible = false
  private launcherLeft = 0
  private launcherTop = 0
  private hasCustomLauncherPosition = false
  private suppressLauncherClick = false
  private detachLauncherDragHandlers: (() => void) | null = null
  private chatLauncherObserver: MutationObserver | null = null

  constructor(private readonly onVisibilityChange?: (visible: boolean) => void) {}

  private notifyVisibilityChange(visible: boolean) {
    this.onVisibilityChange?.(visible)
  }

  private getChatFELauncher() {
    if (typeof document === 'undefined') {
      return null
    }

    return document.getElementById(CHAT_FE_LAUNCHER_ID) as HTMLElement | null
  }

  private getLauncherSize() {
    return {
      width: this.launcherButton?.offsetWidth || 64,
      height: this.launcherButton?.offsetHeight || 64,
    }
  }

  private getDefaultLauncherTop() {
    const { height } = this.getLauncherSize()
    return Math.max(window.innerHeight - height - LAUNCHER_VERTICAL_MARGIN, LAUNCHER_VERTICAL_MARGIN)
  }

  private getDefaultLauncherPosition(): LauncherPosition {
    const { width } = this.getLauncherSize()
    return {
      left: Math.max(window.innerWidth - width - DEFAULT_LAUNCHER_RIGHT, 0),
      top: this.getDefaultLauncherTop(),
    }
  }

  private clampLauncherPosition(position: LauncherPosition): LauncherPosition {
    const { width, height } = this.getLauncherSize()
    const maxLeft = Math.max(window.innerWidth - width, 0)
    const maxTop = Math.max(
      window.innerHeight - height - LAUNCHER_VERTICAL_MARGIN,
      LAUNCHER_VERTICAL_MARGIN,
    )

    return {
      left: Math.min(Math.max(position.left, 0), maxLeft),
      top: Math.min(Math.max(position.top, LAUNCHER_VERTICAL_MARGIN), maxTop),
    }
  }

  private applyLauncherPosition(position: LauncherPosition) {
    if (!this.launcherButton) {
      return
    }

    const nextPosition = this.clampLauncherPosition(position)
    this.launcherLeft = nextPosition.left
    this.launcherTop = nextPosition.top
    this.launcherButton.style.left = `${this.launcherLeft}px`
    this.launcherButton.style.top = `${this.launcherTop}px`
    this.launcherButton.style.right = 'auto'
  }

  private readStoredLauncherPosition(): LauncherPosition | null {
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const rawPosition = window.localStorage.getItem(LAUNCHER_POSITION_STORAGE_KEY)
      if (!rawPosition) {
        return null
      }

      const parsedPosition = JSON.parse(rawPosition) as Partial<LauncherPosition>
      if (
        typeof parsedPosition.left !== 'number' ||
        typeof parsedPosition.top !== 'number' ||
        !Number.isFinite(parsedPosition.left) ||
        !Number.isFinite(parsedPosition.top)
      ) {
        return null
      }

      return {
        left: parsedPosition.left,
        top: parsedPosition.top,
      }
    } catch {
      return null
    }
  }

  private persistLauncherPosition() {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.setItem(
        LAUNCHER_POSITION_STORAGE_KEY,
        JSON.stringify({
          left: Math.round(this.launcherLeft),
          top: Math.round(this.launcherTop),
        }),
      )
    } catch {
      // Ignore storage failures caused by private mode or host-page restrictions.
    }
  }

  private syncLauncherPositionWithChatFE() {
    const chatLauncher = this.getChatFELauncher()
    if (!this.launcherButton) {
      return
    }

    if (this.hasCustomLauncherPosition) {
      this.applyLauncherPosition({
        left: this.launcherLeft,
        top: this.launcherTop,
      })
      return
    }

    if (!chatLauncher || chatLauncher.style.display === 'none') {
      this.applyLauncherPosition(this.getDefaultLauncherPosition())
      return
    }

    const chatRect = chatLauncher.getBoundingClientRect()
    const spacing = 16
    const { width: launcherWidth, height: launcherHeight } = this.getLauncherSize()
    const maxTop = Math.max(
      window.innerHeight - launcherHeight - LAUNCHER_VERTICAL_MARGIN,
      LAUNCHER_VERTICAL_MARGIN,
    )
    const left = Math.max(window.innerWidth - launcherWidth - AVOID_CHAT_FE_LAUNCHER_RIGHT, 0)

    let nextTop = Math.round(chatRect.bottom + spacing)
    if (nextTop > maxTop) {
      nextTop = Math.round(chatRect.top - launcherHeight - spacing)
    }

    this.applyLauncherPosition({
      left,
      top: nextTop,
    })
  }

  private observeChatFELauncher() {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') {
      return
    }

    this.chatLauncherObserver?.disconnect()
    this.syncLauncherPositionWithChatFE()

    const observer = new MutationObserver(() => {
      this.syncLauncherPositionWithChatFE()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    this.chatLauncherObserver = observer
  }

  mount(config: NormalizedSDKConfig) {
    this.destroy()

    if (config.mountMode === 'floating') {
      return this.mountFloating(config)
    }

    return this.mountInline(config)
  }

  private mountInline(config: NormalizedSDKConfig) {
    const container = ensureHTMLElement(config.container)
    const iframe = document.createElement('iframe')
    iframe.src = resolveIframeUrl(config)
    iframe.title = 'ECMAS SDK iframe'
    iframe.allow = 'microphone'
    iframe.style.width = formatDimension(config.resize.width, '420px')
    iframe.style.height = formatDimension(config.resize.height, '720px')
    iframe.style.border = '0'
    iframe.style.display = config.autoShow ? 'block' : 'none'
    iframe.style.background = '#ffffff'
    iframe.style.borderRadius = '24px'
    iframe.dataset.ecmasSdk = 'true'

    container.appendChild(iframe)
    this.iframe = iframe
    return iframe
  }

  private mountFloating(config: NormalizedSDKConfig) {
    const container = ensureHTMLElement(config.container)
    const launcherLogoUrl = resolveLauncherLogoUrl(config)
    const floatingRoot = document.createElement('div')
    floatingRoot.style.position = 'fixed'
    floatingRoot.style.inset = '0'
    floatingRoot.style.zIndex = '2147483000'
    floatingRoot.style.pointerEvents = 'none'
    floatingRoot.style.display = 'none'

    const backdrop = document.createElement('div')
    backdrop.style.position = 'absolute'
    backdrop.style.inset = '0'
    backdrop.style.background = 'rgba(2, 8, 23, 0.42)'
    backdrop.style.opacity = '0'
    backdrop.style.transition = 'opacity 160ms ease'
    backdrop.style.pointerEvents = 'none'
    backdrop.addEventListener('click', () => {
      this.hide()
    })

    const panel = document.createElement('div')
    panel.style.position = 'absolute'
    panel.style.top = '0'
    panel.style.right = '0'
    panel.style.width = '90vw'
    panel.style.height = '100vh'
    panel.style.maxWidth = '90vw'
    panel.style.maxHeight = '100vh'
    panel.style.overflow = 'hidden'
    panel.style.borderRadius = '0'
    panel.style.background = '#ffffff'
    panel.style.boxShadow = '-24px 0 60px rgba(15, 23, 42, 0.22)'
    panel.style.borderLeft = '0'
    panel.style.transformOrigin = 'right center'
    panel.style.transition = 'opacity 160ms ease, transform 160ms ease'
    panel.style.opacity = '0'
    panel.style.transform = 'translateX(24px)'
    panel.style.pointerEvents = 'none'

    const iframe = document.createElement('iframe')
    iframe.src = resolveIframeUrl(config)
    iframe.title = 'ECMAS SDK iframe'
    iframe.allow = 'microphone'
    iframe.style.width = '100%'
    iframe.style.height = '100%'
    iframe.style.border = '0'
    iframe.style.background = '#ffffff'
    iframe.dataset.ecmasSdk = 'true'
    panel.appendChild(iframe)

    const launcherButton = document.createElement('button')
    launcherButton.type = 'button'
    launcherButton.title = 'Open agent dashboard'
    launcherButton.setAttribute('aria-label', 'Open agent dashboard')
    launcherButton.style.position = 'fixed'
    launcherButton.style.left = '0'
    launcherButton.style.zIndex = '2147483000'
    launcherButton.style.pointerEvents = 'auto'
    launcherButton.style.width = '64px'
    launcherButton.style.height = '64px'
    launcherButton.style.border = '0'
    launcherButton.style.borderRadius = '9999px'
    launcherButton.style.cursor = 'pointer'
    launcherButton.style.padding = '0'
    launcherButton.style.background = `center / contain no-repeat url("${launcherLogoUrl}")`
    launcherButton.style.backgroundColor = 'transparent'
    launcherButton.style.boxShadow = '0 18px 40px rgba(15, 23, 42, 0.18)'
    launcherButton.style.transition = 'opacity 160ms ease, transform 160ms ease'
    launcherButton.style.touchAction = 'none'
    launcherButton.style.display = 'none'

    launcherButton.addEventListener('click', () => {
      if (this.suppressLauncherClick) {
        this.suppressLauncherClick = false
        return
      }
      this.toggle()
    })

    launcherButton.addEventListener('mouseenter', () => {
      this.isLauncherHovered = true
      this.updateLauncherButtonState()
    })

    launcherButton.addEventListener('mouseleave', () => {
      this.isLauncherHovered = false
      this.updateLauncherButtonState()
    })

    this.attachLauncherDrag(launcherButton)

    floatingRoot.appendChild(backdrop)
    floatingRoot.appendChild(panel)
    container.appendChild(floatingRoot)
    container.appendChild(launcherButton)

    this.iframe = iframe
    this.floatingRoot = floatingRoot
    this.backdrop = backdrop
    this.shell = panel
    this.launcherButton = launcherButton
    const storedLauncherPosition = this.readStoredLauncherPosition()
    this.hasCustomLauncherPosition = Boolean(storedLauncherPosition)
    this.applyLauncherPosition(storedLauncherPosition ?? this.getDefaultLauncherPosition())
    this.observeChatFELauncher()
    this.setFloatingOpen(config.autoShow)
    this.setLauncherVisible(false)
    return iframe
  }

  private attachLauncherDrag(launcherButton: HTMLButtonElement) {
    let pointerId: number | null = null
    let dragStartX = 0
    let dragStartY = 0
    let dragStartLeft = 0
    let dragStartTop = 0
    let moved = false

    const handlePointerMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      const deltaX = event.clientX - dragStartX
      const deltaY = event.clientY - dragStartY
      if (Math.hypot(deltaX, deltaY) > LAUNCHER_DRAG_THRESHOLD) {
        moved = true
        if (!this.hasCustomLauncherPosition) {
          this.hasCustomLauncherPosition = true
          this.updateLauncherButtonState()
        }
      }

      this.applyLauncherPosition({
        left: dragStartLeft + deltaX,
        top: dragStartTop + deltaY,
      })
    }

    const cleanupDrag = (shouldSuppressClick: boolean) => {
      const activePointerId = pointerId
      if (activePointerId === null) {
        moved = false
        return
      }

      pointerId = null
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
      window.removeEventListener('blur', handleWindowBlur)
      if (launcherButton.hasPointerCapture(activePointerId)) {
        launcherButton.releasePointerCapture(activePointerId)
      }
      if (shouldSuppressClick && moved) {
        this.suppressLauncherClick = true
        this.persistLauncherPosition()
      }
      moved = false
    }

    const handlePointerUp = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      cleanupDrag(true)
    }

    const handleWindowBlur = () => {
      cleanupDrag(true)
    }

    const handleLostPointerCapture = () => {
      cleanupDrag(true)
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (this.isFloatingOpen) return
      cleanupDrag(false)
      pointerId = event.pointerId
      dragStartX = event.clientX
      dragStartY = event.clientY
      dragStartLeft = this.hasCustomLauncherPosition
        ? this.launcherLeft
        : launcherButton.getBoundingClientRect().left
      dragStartTop = this.launcherTop
      moved = false
      launcherButton.setPointerCapture(event.pointerId)
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
      window.addEventListener('pointercancel', handlePointerUp)
      window.addEventListener('blur', handleWindowBlur)
    }

    launcherButton.addEventListener('pointerdown', handlePointerDown)
    launcherButton.addEventListener('lostpointercapture', handleLostPointerCapture)

    this.detachLauncherDragHandlers = () => {
      launcherButton.removeEventListener('pointerdown', handlePointerDown)
      launcherButton.removeEventListener('lostpointercapture', handleLostPointerCapture)
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }

  private updateLauncherButtonState() {
    if (!this.launcherButton) return

    const hiddenTransform = this.hasCustomLauncherPosition
      ? 'scale(0.92)'
      : 'translateX(24px) scale(0.92)'
    const visibleTransform = this.hasCustomLauncherPosition
      ? this.isLauncherHovered
        ? 'scale(1.04)'
        : 'scale(1)'
      : this.isLauncherHovered
        ? 'translateX(0) scale(1)'
        : 'translateX(14px) scale(1)'

    this.launcherButton.style.display = this.isLauncherVisible ? 'flex' : 'none'
    if (!this.isLauncherVisible) {
      this.launcherButton.style.opacity = '0'
      this.launcherButton.style.pointerEvents = 'none'
      this.launcherButton.style.transform = hiddenTransform
      return
    }

    if (this.isFloatingOpen) {
      this.launcherButton.style.opacity = '0'
      this.launcherButton.style.pointerEvents = 'none'
      this.launcherButton.style.transform = hiddenTransform
      return
    }

    this.launcherButton.style.opacity = '1'
    this.launcherButton.style.pointerEvents = 'auto'
    this.launcherButton.style.transform = visibleTransform
  }

  private setFloatingOpen(visible: boolean) {
    const visibilityChanged = this.isFloatingOpen !== visible
    this.isFloatingOpen = visible
    if (!this.shell || !this.launcherButton || !this.floatingRoot || !this.backdrop) return

    this.floatingRoot.style.display = visible ? 'block' : 'none'
    this.floatingRoot.style.pointerEvents = 'none'
    this.backdrop.style.opacity = visible ? '1' : '0'
    this.backdrop.style.pointerEvents = visible ? 'auto' : 'none'
    this.shell.style.opacity = visible ? '1' : '0'
    this.shell.style.pointerEvents = visible ? 'auto' : 'none'
    this.shell.style.transform = visible ? 'translateX(0)' : 'translateX(24px)'
    this.shell.style.display = 'block'
    this.launcherButton.setAttribute('aria-expanded', visible ? 'true' : 'false')
    this.updateLauncherButtonState()

    if (visibilityChanged) {
      this.notifyVisibilityChange(visible)
    }
  }

  setLauncherVisible(visible: boolean) {
    this.isLauncherVisible = visible
    if (!visible && this.isFloatingOpen) {
      this.setFloatingOpen(false)
      return
    }

    if (visible) {
      this.syncLauncherPositionWithChatFE()
    }

    this.updateLauncherButtonState()
  }

  getIframe() {
    return this.iframe
  }

  show() {
    if (this.shell) {
      this.setFloatingOpen(true)
      return true
    }

    if (this.iframe) {
      this.iframe.style.display = 'block'
      this.notifyVisibilityChange(true)
    }

    return true
  }

  hide() {
    if (this.shell) {
      this.setFloatingOpen(false)
      return false
    }

    if (this.iframe) {
      this.iframe.style.display = 'none'
      this.notifyVisibilityChange(false)
    }

    return false
  }

  toggle() {
    if (this.shell) {
      const nextVisible = !this.isFloatingOpen
      this.setFloatingOpen(nextVisible)
      return nextVisible
    }

    if (this.iframe) {
      const nextVisible = this.iframe.style.display === 'none'
      this.iframe.style.display = nextVisible ? 'block' : 'none'
      this.notifyVisibilityChange(nextVisible)
      return nextVisible
    }

    return false
  }

  resize(options: SDKResizeOptions) {
    if (this.shell) {
      if (options.width !== undefined) {
        this.shell.style.width = formatDimension(options.width, '90vw')
        this.shell.style.maxWidth = formatDimension(options.width, '90vw')
      }
      if (options.height !== undefined) {
        this.shell.style.height = formatDimension(options.height, '100vh')
        this.shell.style.maxHeight = formatDimension(options.height, '100vh')
      }
      return
    }

    if (this.iframe) {
      if (options.width !== undefined) {
        this.iframe.style.width = formatDimension(options.width, '420px')
      }
      if (options.height !== undefined) {
        this.iframe.style.height = formatDimension(options.height, '720px')
      }
    }
  }

  destroy() {
    this.detachLauncherDragHandlers?.()
    this.chatLauncherObserver?.disconnect()
    this.floatingRoot?.remove()
    this.shell?.remove()
    this.iframe?.remove()
    this.floatingRoot = null
    this.backdrop = null
    this.shell = null
    this.launcherButton = null
    this.iframe = null
    this.isFloatingOpen = false
    this.isLauncherHovered = false
    this.isLauncherVisible = false
    this.launcherLeft = 0
    this.launcherTop = 0
    this.hasCustomLauncherPosition = false
    this.suppressLauncherClick = false
    this.detachLauncherDragHandlers = null
    this.chatLauncherObserver = null
  }
}
